/**
 * MechaGuard - Tower (防御塔基类)
 *
 * 通用逻辑：
 *   - 网格坐标 <-> 世界坐标
 *   - 目标选择（优先攻击「走得最远」的敌人，塔防经典策略）
 *   - 炮塔平滑转向
 *   - 等级成长（Lv2 = 伤害 x1.3，Lv3 = 再 x1.5，射程每级 +8%）
 *   - 底座 / 炮塔 / 等级指示 / 射程圈 的绘制
 *
 * 子类只需实现：
 *   - static META   { id, name, icon, cost, desc, color }
 *   - baseRange / baseDamage / fireInterval
 *   - update(dt, game)
 *   - drawTurret(ctx)
 */

import { TILE_SIZE } from '../maps/MapData.js';
import { balanceConfig } from '../config/BalanceConfig.js';

export class Tower {
  /** 子类必须覆盖 */
  static META = {
    id: 'base',
    name: '防御塔',
    icon: '?',
    cost: 100,
    desc: '',
    color: '#00ffff',
  };

  /** 战斗数值默认值；子类在 META.stats 里覆盖需要调的项 */
  static DEFAULT_STATS = {
    baseRange: 120,
    baseDamage: 10,
    fireInterval: 1,
    barrelLength: 16,
    turnSpeed: 10,     // 炮塔转向速度（弧度/秒）
    firingFx: 0.1,     // 开火后坐动画时长
  };

  constructor(col, row) {
    this.col = col;
    this.row = row;
    this.x = (col + 0.5) * TILE_SIZE;
    this.y = (row + 0.5) * TILE_SIZE;

    this.level = 1;
    this.maxLevel = 3;

    this.angle = -Math.PI / 2; // 初始朝向：上方
    this.aimAngle = this.angle;
    this.cooldown = 0;

    this.age = 0;
    this.firing = 0;        // 开火后坐动画计时
    this.totalDamage = 0;

    this.invested = balanceConfig.getTowerCost(this.id, this.constructor.META.cost);

    // 战斗数值：默认(DEFAULT_STATS + META.stats) 叠加运营覆盖层，便于集中调平衡
    const stats = {
      ...Tower.DEFAULT_STATS,
      ...balanceConfig.getTowerStats(this.constructor.META.id, this.constructor.META.stats),
    };
    Object.assign(this, stats);

    // baseRange / baseDamage / fireInterval 是 range / damage / dps 取值的来源
    this.baseRange = stats.baseRange;
    this.baseDamage = stats.baseDamage;
    this.fireInterval = stats.fireInterval;
  }

  /** 实时套用新数值（运营视图改塔后立即作用到场上已有塔） */
  applyStats(stats) {
    Object.assign(this, stats);
    this.baseRange = stats.baseRange ?? this.baseRange;
    this.baseDamage = stats.baseDamage ?? this.baseDamage;
    this.fireInterval = stats.fireInterval ?? this.fireInterval;
  }

  /* ===================================================
   * 元数据与成长
   * =================================================== */

  get meta() {
    return this.constructor.META;
  }

  get id() {
    return this.meta.id;
  }

  get name() {
    return this.meta.name;
  }

  get color() {
    return this.meta.color;
  }

  /** 等级伤害倍率：Lv2 = x1.3，Lv3 = x1.3 * x1.5 = x1.95 */
  get levelMultiplier() {
    if (this.level >= 3) return 1.95;
    if (this.level === 2) return 1.3;
    return 1;
  }

  get damage() {
    return this.baseDamage * this.levelMultiplier;
  }

  /** 射程每级 +8% */
  get range() {
    return this.baseRange * (1 + (this.level - 1) * 0.08);
  }

  /** 每秒伤害（用于 UI 展示） */
  get dps() {
    return this.fireInterval > 0 ? this.damage / this.fireInterval : 0;
  }

  canUpgrade() {
    return this.level < this.maxLevel;
  }

  getUpgradeCost() {
    if (!this.canUpgrade()) return Infinity;
    const base = balanceConfig.getTowerCost(this.id, this.meta.cost);
    // Lv1 -> Lv2 : x1.5 ; Lv2 -> Lv3 : x2.5
    return this.level === 1 ? Math.round(base * 1.5) : Math.round(base * 2.5);
  }

  /** @returns {boolean} 是否升级成功 */
  upgrade() {
    if (!this.canUpgrade()) return false;
    this.invested += this.getUpgradeCost();
    this.level += 1;
    return true;
  }

  /** 出售返还 60% 累计投入 */
  getSellValue() {
    return Math.floor(this.invested * 0.6);
  }

  /* ===================================================
   * 目标选择
   * =================================================== */

  /**
   * 选择射程内「沿路径走得最远」的敌人
   * @param {import('./Enemy.js').Enemy[]} enemies
   * @param {number} [range]
   * @returns {import('./Enemy.js').Enemy|null}
   */
  findTarget(enemies, range = this.range) {
    const r2 = range * range;
    let best = null;

    for (let i = 0; i < enemies.length; i++) {
      const e = enemies[i];
      if (!e.alive || e.reachedBase) continue;
      if (e.distanceSqTo(this.x, this.y) > r2) continue;
      if (best === null || e.pathProgress > best.pathProgress) best = e;
    }

    return best;
  }

  /** 选择射程内最近的敌人（冰冻塔用） */
  findClosest(enemies, range = this.range) {
    const r2 = range * range;
    let best = null;
    let bestSq = Infinity;

    for (let i = 0; i < enemies.length; i++) {
      const e = enemies[i];
      if (!e.alive || e.reachedBase) continue;
      const d = e.distanceSqTo(this.x, this.y);
      if (d <= r2 && d < bestSq) {
        bestSq = d;
        best = e;
      }
    }

    return best;
  }

  /**
   * 炮塔平滑转向目标
   * @param {{x:number,y:number}} target
   * @param {number} dt
   * @param {number} turnSpeed 弧度/秒
   */
  aimAt(target, dt, turnSpeed = 10) {
    const desired = Math.atan2(target.y - this.y, target.x - this.x);
    this.aimAngle = desired;

    let diff = desired - this.angle;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;

    const step = turnSpeed * dt;
    if (Math.abs(diff) <= step) {
      this.angle = desired;
    } else {
      this.angle += Math.sign(diff) * step;
    }

    return diff;
  }

  /* ===================================================
   * 更新（子类覆盖）
   * =================================================== */

  /**
   * @param {number} dt
   * @param {import('../core/Game.js').Game} game
   */
  update(dt, game) { // eslint-disable-line no-unused-vars
    this.age += dt;
  }

  /** 共用的计时器推进，子类可在 update 开头调用 */
  tickTimers(dt) {
    this.age += dt;
    if (this.cooldown > 0) this.cooldown -= dt;
    if (this.firing > 0) this.firing -= dt;
  }

  /* ===================================================
   * 绘制
   * =================================================== */

  /**
   * 绘制整座塔
   * @param {CanvasRenderingContext2D} ctx
   * @param {{selected?:boolean, preview?:boolean, invalid?:boolean}} [opts]
   */
  draw(ctx, opts = {}) {
    const { selected = false, preview = false, invalid = false } = opts;

    ctx.save();
    ctx.globalAlpha = preview ? 0.65 : 1;
    ctx.translate(this.x, this.y);

    // ---- 底座平台 ----
    const accent = invalid ? '#ff3366' : this.color;

    ctx.shadowColor = accent;
    ctx.shadowBlur = preview ? 6 : 12;

    ctx.beginPath();
    for (let i = 0; i < 8; i++) {
      const a = (Math.PI / 4) * i + Math.PI / 8;
      const r = TILE_SIZE * 0.38;
      const px = Math.cos(a) * r;
      const py = Math.sin(a) * r;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();

    ctx.fillStyle = 'rgba(10, 24, 42, 0.92)';
    ctx.fill();
    ctx.strokeStyle = accent;
    ctx.lineWidth = 2;
    ctx.stroke();

    // ---- 内部刻度环 ----
    ctx.shadowBlur = 0;
    ctx.strokeStyle = `${accent}55`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(0, 0, TILE_SIZE * 0.27, 0, Math.PI * 2);
    ctx.stroke();

    // ---- 炮塔本体（旋转） ----
    ctx.save();
    ctx.rotate(this.angle);
    const recoil = this.firing > 0 ? 3 : 0;
    ctx.translate(-recoil, 0);
    this.drawTurret(ctx, accent);
    ctx.restore();

    // ---- 等级指示 ----
    if (!preview) this._drawLevelPips(ctx, accent);

    ctx.restore();

    if (selected) this.drawRange(ctx);
  }

  /**
   * 绘制炮塔（局部坐标，原点在塔中心，+X 为炮口方向）
   * 子类覆盖。
   */
  drawTurret(ctx, accent) {
    ctx.fillStyle = accent;
    ctx.beginPath();
    ctx.arc(0, 0, 7, 0, Math.PI * 2);
    ctx.fill();
  }

  /** 射程圈 + 瞄准辅助 */
  drawRange(ctx) {
    ctx.save();
    ctx.translate(this.x, this.y);

    ctx.beginPath();
    ctx.arc(0, 0, this.range, 0, Math.PI * 2);
    ctx.fillStyle = `${this.color}12`;
    ctx.fill();

    ctx.setLineDash([6, 6]);
    ctx.strokeStyle = `${this.color}bb`;
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.restore();
  }

  _drawLevelPips(ctx, accent) {
    const total = this.maxLevel;
    const size = 4;
    const gap = 3;
    const width = total * size + (total - 1) * gap;
    const startX = -width / 2;
    const y = TILE_SIZE * 0.3;

    for (let i = 0; i < total; i++) {
      const x = startX + i * (size + gap);
      ctx.fillStyle = i < this.level ? accent : 'rgba(255,255,255,0.18)';
      ctx.fillRect(x, y, size, size);
    }
  }
}
